import React, { useState } from 'react'
import Card from 'components/common/cards/Card'
import Text from 'components/common/Text'
import CardTitle from 'components/common/cards/CardTitle'
import { usePlans } from 'hooks/api/usePlans'
import colors from 'utility/colors'
import { formatMoneyCents } from 'utility/numbers'
import Icon from '../Icon'
import { useApp } from 'components/providers/AppProvider'
import { useTenantProfile } from 'hooks/api/tenant/useTenantProfile'

const PlanCard = ({ plan, isCurrentPlan, handlePlanChange, featureGroupIndex }) => {
	const { data: tenantProfile } = useTenantProfile()
	const secondaryColor = tenantProfile?.theme?.secondaryColor
	const primaryColor = tenantProfile?.theme?.primaryColor

	const shouldRenderAdditionalFeatures = featureGroupIndex !== undefined && featureGroupIndex > 0

	const featuresToRender =
		featureGroupIndex !== undefined
			? plan?.featureGroups?.[plan.featureGroups?.length - 1]?.features || []
			: plan?.featureGroups?.flatMap((group) => group.features) || []

	return (
		<Card
			noShadow
			style={{
				position: 'relative',
				borderWidth: 6,
				borderStyle: 'solid',
				borderColor: isCurrentPlan ? primaryColor : colors.MUTED_GRAY,
				minWidth: 250,
				padding: 15,
				flex: 1,
				marginBottom: 0,
			}}
			onClick={() => handlePlanChange?.(plan)}
		>
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: 20,
				}}
			>
				<div>
					<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							gap: 10,
							justifyContent: 'space-between',
						}}
					>
						<Text size={22} bold color={secondaryColor}>
							{plan.planName}
						</Text>
					</div>
					{plan.canApplyPartnerDiscount && plan.partnerDiscountPct ? (
						<div
							style={{
								position: 'absolute',
								top: -25,
								right: -10,
								background: '#fff',
								paddingLeft: 10,
								paddingRight: 10,
								borderRadius: 5,
								paddingTop: 5,
								paddingBottom: 5,
								transform: 'rotate(6deg)',
								border: `3px solid ${secondaryColor}`,
							}}
						>
							<Text
								size={18}
								bold
								color={secondaryColor}
								style={{
									paddingBottom: 0,
									marginBottom: 0,
								}}
							>
								{plan.partnerDiscountPct}% off!
							</Text>
						</div>
					) : null}
					{plan.membershipFeeAmountInCents ? (
						<Text size={18} bold color={colors.FOOTER} style={{ marginBottom: 0 }}>
							$
							{plan.canApplyPartnerDiscount && plan.partnerDiscountPct ? (
								<>
									<s>
										{formatMoneyCents(plan.membershipFeeAmountInCents)}
										{plan.planName?.isLifetime ? ' one-time' : '/year'}
									</s>{' '}
									${formatMoneyCents(plan.partnerDiscountedFeeAmtInCents)}
								</>
							) : (
								formatMoneyCents(plan.membershipFeeAmountInCents)
							)}
							{plan.isLifetime ? ' one-time' : '/year'}
						</Text>
					) : null}
					<Text size={18} bold color={colors.FOOTER} style={{ marginBottom: 0 }}>
						<span dangerouslySetInnerHTML={{ __html: plan.successFeeText }} />
					</Text>
				</div>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
					{shouldRenderAdditionalFeatures ? (
						<Text size={18} bold color={secondaryColor}>
							Additional features included:
						</Text>
					) : null}
					<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
						{featuresToRender.map((feature, index) => (
							<div
								key={index}
								style={{
									display: 'flex',
									flexDirection: 'row',
									gap: 10,
									alignItems: 'center',
								}}
							>
								<div style={{ minWidth: 20 }}>
									<Icon icon='check' solid size={18} color={secondaryColor} />
								</div>
								<Text style={{ paddingBottom: 0, marginBottom: 0 }}>{feature}</Text>
							</div>
						))}
					</div>
				</div>
			</div>
		</Card>
	)
}

export const PlanSelectionBlock = ({
	title = 'Plan',
	handlePlanChange,
	backgroundColor = colors.MUTED_GRAY,
	planId,
	highlightedPlanId,
}) => {
	const { data: plans } = usePlans({})
	const { breakpoints } = useApp()

	const availablePlans = plans?.availablePlans || []
	const currentPlan = plans?.currentPlan

	const preSelectedLockedInPlan = [
		...(plans?.availablePlans || []),
		...(plans?.alternatePlans || []),
		plans?.alternatePlans?.[0],
	].find((plan) => plan?.planId === planId)

	if (!plans?.availablePlans && !plans?.currentPlan) return null

	return (
		<>
			<Card noShadow background={backgroundColor} padding={20}>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
					<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							justifyContent: 'space-between',
						}}
					>
						<CardTitle align={'left'}>{title}</CardTitle>
					</div>

					<div
						style={{
							display: 'flex',
							flexDirection: breakpoints.lg ? 'row' : 'column',
							gap: 10,
							flexWrap: 'wrap',
						}}
					>
						{preSelectedLockedInPlan ? (
							<PlanCard
								plan={preSelectedLockedInPlan}
								isCurrentPlan={true}
								handlePlanChange={handlePlanChange}
							/>
						) : (
							availablePlans.map((plan, index) => {
								let isCurrentPlan
								if (highlightedPlanId) {
									isCurrentPlan = highlightedPlanId === plan.planId
								} else {
									isCurrentPlan = currentPlan?.planId === plan.planId
								}

								return (
									<PlanCard
										key={index}
										plan={plan}
										isCurrentPlan={isCurrentPlan}
										handlePlanChange={handlePlanChange}
										featureGroupIndex={index}
									/>
								)
							})
						)}
					</div>
				</div>
			</Card>
		</>
	)
}
